import { AnalyticsEvent, AnalyticsManager } from '@oy/analytics';
import { toast } from '@oy/uidesktop';
import { useEffect, useState } from 'react';
import { disableAutoReportService } from 'src/clients';
import { useTranslate, useUserRole } from 'src/hooks';
import { useAutoReportData } from '../../hooks';

export const useAutoReportContext = ({
  productName,
  showSideModalParent = false,
}: {
  productName?: string;
  showSideModalParent?: boolean;
}) => {
  const [selectedProduct, setSelectedProduct] = useState<string | undefined>(
    undefined,
  );
  const [showSideModal, setShowSideModal] = useState(false);
  const [showCancelReportModal, setShowCancelReportModal] = useState(false);
  const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(false);
  const [isEditConfig, setIsEditConfig] = useState(false);
  const [clickFrom, setClickFrom] = useState('auto_report_page');
  const { isSuperAdmin, isAdmin } = useUserRole();

  const {
    data,
    mutate,
    isValidating,
    isLoading: isLoadingData,
  } = useAutoReportData(isSuperAdmin || isAdmin);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceEnableField, setForceEnableField] = useState(false); // state for force enable field used in product page
  const [isActive, setIsActive] = useState(false); // state for toggle activation in sidemodal
  const isLoading = isValidating || isLoadingData || isSubmitting;
  const { getTranslation } = useTranslate();

  const onShowSideModal = (product_name: string, isFromProduct: boolean) => {
    isFromProduct
      ? setClickFrom('export_modal')
      : setClickFrom('auto_report_page');
    setSelectedProduct(product_name);
    const element = data?.find(
      (element: any) => element.product_name === product_name,
    );
    setIsEditConfig(element?.is_auto_report_active ?? false);
    setShowSideModal(true);
    setForceEnableField(true);
  };

  const onCloseSideModal = () => {
    setIsEditConfig(false);
    setSelectedProduct(undefined);
    setShowSideModal(false);
    setForceEnableField(false);
    setIsActive(false);
  };

  const onShowDisableReportModal = (product_name: string) => {
    setSelectedProduct(product_name);
    setShowCancelReportModal(true);
    setIsEditConfig(false);
  };

  const onShowDisableReportModalFromProduct = () => {
    setShowCancelReportModal(true);
    setShowSideModal(false);
  };

  const onCloseDisableReportModal = (isFromModal?: boolean) => {
    // if from modal, open modal again (used on auto report in product page)
    if (isFromModal) {
      setShowCancelReportModal(false);
      setShowSideModal(true);
    } else {
      setSelectedProduct(undefined);
      setIsEditConfig(false);
    }
    setShowCancelReportModal(false);
  };

  const onDisable = async (isFromModal?: boolean) => {
    try {
      setIsSubmitting(true);
      const response = await disableAutoReportService(selectedProduct!);
      if (response.data.success) {
        AnalyticsManager.instance().track(
          AnalyticsEvent.auto_report_deactive_success,
          {
            product: selectedProduct?.toLowerCase(),
            click_from: clickFrom,
          },
        );
        toast.success(getTranslation('settings_autoReport_nonActive'));
        mutate();
        return;
      }
      toast.error(
        getTranslation('settings_autoReport_message_failedDisableAutoReport'),
      );
      if (isFromModal) onCloseSideModal();
    } catch (e) {
      toast.error(
        getTranslation('settings_autoReport_message_failedDisableAutoReport'),
      );
      if (isFromModal) setShowSideModal(true);
    } finally {
      setIsSubmitting(false);
      onCloseDisableReportModal();
    }
  };

  const onToggleActivation = () => {
    setForceEnableField(!isActive);
    setIsActive(!isActive);
    setIsErrorMessageVisible(false);
  };

  useEffect(() => {
    if (showSideModalParent && productName) {
      onShowSideModal(productName, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productName, showSideModalParent]);

  return {
    showSideModal,
    selectedProduct,
    showCancelReportModal,
    isEditConfig,
    isLoading,
    forceEnableField,
    isActive,
    isErrorMessageVisible,
    clickFrom,
    onDisable,
    onShowSideModal,
    onCloseSideModal,
    onShowDisableReportModal,
    onCloseDisableReportModal,
    setIsActive,
    setIsErrorMessageVisible,
    onToggleActivation,
    onShowDisableReportModalFromProduct,
  };
};
